// frontend/src/components/SidebarNavGroup.js
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { isGroupExpanded, defaultChildPath } from './sidebarNavUtils';
import { useAppearance } from '../contexts/AppearanceContext';

const CountBadge = ({ count }) => (
  <span className="ml-auto inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 text-xs font-bold text-white bg-red-500 rounded-full">
    {count}
  </span>
);

// Forest nav row (design-handoff/forest-theme §Phase 0.6). Shared with
// Sidebar.js so flat items and group parents read the same. Fable keeps the
// classes inline below and in Sidebar.js, untouched.
export const forestNavItemClass = ({ active, collapsed = false, tone } = {}) =>
  `flex items-center h-9 rounded-md text-sm transition-colors duration-[120ms] ${
    collapsed ? 'relative justify-center' : 'gap-2.5 px-2.5'
  } ${
    active
      ? 'bg-floral-100 text-floral-900 font-semibold'
      : `${tone === 'blue' ? 'text-floral-700 [html[data-scheme=dark]:not([data-appearance=fable])_&]:text-floral-900' : 'text-gray-600'} font-medium hover:bg-floral-50`
  }`;

export const forestNavIconClass = (active, tone) =>
  `relative flex-shrink-0 w-[18px] h-[18px] grid place-items-center ${
    active ? 'text-floral-700' : tone === 'blue' ? '' : 'text-gray-500'
  }`;

export const ForestCountBadge = ({ count, className = '' }) => (
  <span
    className={`inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-danger text-white text-[11px] font-bold tabular ${className}`}
  >
    {count}
  </span>
);

// Count on an icon in the 56px rail, where there is no room for the pill.
export const ForestRailBadge = ({ count, className = '' }) => (
  <span
    className={`absolute inline-flex items-center justify-center h-4 min-w-4 px-1 rounded-full bg-danger text-white text-[10px] font-bold leading-none tabular ${className}`}
  >
    {count}
  </span>
);

const SidebarNavGroup = ({ group, isCollapsed, orderCount = 0 }) => {
  const { pathname } = useLocation();
  const { appearance } = useAppearance();
  const isForest = appearance !== 'fable';
  const Badge = isForest ? ForestCountBadge : CountBadge;
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const iconRef = useRef(null);
  const wrapperRef = useRef(null);
  const flyoutRef = useRef(null);
  const closeTimer = useRef(null);
  const expanded = isGroupExpanded(pathname, group);
  const defaultPath = defaultChildPath(group);

  const badgeChildPath = group.children.find((c) => c.badge === 'orderCount')?.path;
  const hasBadge = orderCount > 0 && !!badgeChildPath;
  const childrenInline = !isCollapsed && expanded;
  const badgeOnParent = hasBadge && !childrenInline;

  useEffect(() => () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }, []);

  // The collapsed flyout is rendered in a portal on document.body with
  // position:fixed at computed coordinates. The portal escapes both (a) the
  // sidebar's overflow-y-auto scroll container (which forces overflow-x:hidden
  // and would clip an absolutely positioned flyout) and (b) any nested
  // stacking context — e.g. the events board's transformed runway rows and
  // their z-30 hover labels — that would otherwise paint over a z-index scoped
  // inside the sidebar subtree. Because the flyout is no longer a DOM
  // descendant of the wrapper, hover/focus handoff is managed explicitly via a
  // short close delay and relatedTarget checks against both the wrapper and
  // the flyout.
  const openFlyout = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    const el = iconRef.current;
    if (el) {
      const r = el.getBoundingClientRect();
      setCoords({ top: r.top, left: r.right + 8 });
    }
    setFlyoutOpen(true);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setFlyoutOpen(false), 150);
  };
  const handleFocusOut = (e) => {
    const rt = e.relatedTarget;
    const w = wrapperRef.current;
    const f = flyoutRef.current;
    if ((w && w.contains(rt)) || (f && f.contains(rt))) return;
    setFlyoutOpen(false);
  };

  if (isCollapsed) {
    return (
      <div
        ref={wrapperRef}
        className="relative"
        onMouseEnter={openFlyout}
        onMouseLeave={scheduleClose}
        onFocus={openFlyout}
        onBlur={handleFocusOut}
      >
        <Link
          ref={iconRef}
          to={defaultPath}
          title={group.label}
          aria-label={group.label}
          aria-expanded={expanded}
          className={isForest ? forestNavItemClass({ active: expanded, collapsed: true }) : `relative flex items-center justify-center px-3 py-2.5 rounded-lg transition-colors ${
            expanded
              ? 'bg-floral-200 text-floral-900 [&_svg]:stroke-[2.5]'
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          }`}
        >
          <span className={isForest ? forestNavIconClass(expanded) : 'flex-shrink-0'}>{group.icon}</span>
          {/* Submenu affordance: signals the icon opens a child menu on hover. */}
          <span
            data-testid="submenu-indicator"
            aria-hidden="true"
            className="absolute right-0.5 top-1/2 -translate-y-1/2 text-gray-400"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" style={{ width: '8px', height: '8px' }}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </span>
          {badgeOnParent && isForest && <ForestRailBadge count={orderCount} className="top-0 right-0" />}
          {badgeOnParent && !isForest && (
            <span className="absolute top-1 right-1 inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">
              {orderCount}
            </span>
          )}
        </Link>

        {flyoutOpen &&
          createPortal(
            <div
              ref={flyoutRef}
              data-testid="nav-flyout"
              style={{ position: 'fixed', top: coords.top, left: coords.left }}
              onMouseEnter={openFlyout}
              onMouseLeave={scheduleClose}
              onBlur={handleFocusOut}
              className="z-50 w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-1"
            >
              <div className="px-3 py-1.5 text-xs font-semibold text-gray-500">{group.label}</div>
              {group.children.map((child) => (
                <NavLink
                  key={child.path}
                  to={child.path}
                  end={child.end === true}
                  className={({ isActive }) =>
                    `flex items-center px-3 py-1.5 text-sm transition-colors ${
                      isActive ? 'text-floral-700 [html[data-scheme=dark]:not([data-appearance=fable])_&]:text-floral-900 font-semibold' : `text-gray-600 ${isForest ? 'hover:bg-floral-50' : 'hover:bg-gray-50'}`
                    }`
                  }
                >
                  <span>{child.label}</span>
                  {child.path === badgeChildPath && orderCount > 0 && <Badge count={orderCount} className="ml-auto" />}
                </NavLink>
              ))}
            </div>,
            document.body
          )}
      </div>
    );
  }

  if (isForest) {
    return (
      <div className="flex flex-col gap-0.5">
        <Link to={defaultPath} aria-expanded={expanded} className={forestNavItemClass({ active: expanded })}>
          <span className={forestNavIconClass(expanded)}>{group.icon}</span>
          <span className="flex-1 truncate">{group.label}</span>
          {badgeOnParent && <ForestCountBadge count={orderCount} />}
        </Link>

        {childrenInline &&
          group.children.map((child) => (
            <NavLink
              key={child.path}
              to={child.path}
              end={child.end === true}
              className={({ isActive }) =>
                `flex items-center h-8 pl-[38px] pr-2.5 rounded-md text-[13px] transition-colors duration-[120ms] ${
                  isActive ? 'text-floral-700 [html[data-scheme=dark]:not([data-appearance=fable])_&]:text-floral-900 font-semibold' : 'text-gray-600 font-medium hover:bg-floral-50'
                }`
              }
            >
              <span className="flex-1 truncate">{child.label}</span>
              {child.path === badgeChildPath && orderCount > 0 && <ForestCountBadge count={orderCount} />}
            </NavLink>
          ))}
      </div>
    );
  }

  return (
    <div>
      <Link
        to={defaultPath}
        aria-expanded={expanded}
        className={`flex items-center px-3 py-2.5 rounded-lg transition-colors ${
          expanded
            ? 'bg-floral-200 text-floral-900 font-bold [&_svg]:stroke-[2.5]'
            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
        }`}
      >
        <span className="flex-shrink-0">{group.icon}</span>
        <span className="ml-3 whitespace-nowrap overflow-hidden">{group.label}</span>
        {badgeOnParent && <CountBadge count={orderCount} />}
      </Link>

      {childrenInline &&
        group.children.map((child) => (
          <NavLink
            key={child.path}
            to={child.path}
            end={child.end === true}
            className={({ isActive }) =>
              `flex items-center pl-11 pr-3 py-2 rounded-lg text-[13px] transition-colors ${
                isActive
                  ? 'text-floral-700 [html[data-scheme=dark]:not([data-appearance=fable])_&]:text-floral-900 font-semibold'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`
            }
          >
            <span className="whitespace-nowrap overflow-hidden">{child.label}</span>
            {child.path === badgeChildPath && orderCount > 0 && <CountBadge count={orderCount} />}
          </NavLink>
        ))}
    </div>
  );
};

export default SidebarNavGroup;
